import {
  useResponsiveFontSize,
  useResponsiveHeight,
  useResponsiveScreenWidth,
} from '@/components/Util/reactResponsiveDimensions';
import { PressableNeolexon } from '@/components/PressableNeolexon';
import { useStoreState } from '@/stores/store';
import { useEffect, useState } from 'react';
import { Linking, Platform, ScrollView, StyleSheet, View } from 'react-native';
import { Text } from '@/components/Text';
import {
  deleteUser,
  fetchUserConsents,
  localLogoutUser,
} from '../api/services/user';
import ConfirmModal from '@/components/SharedComponents/ConfirmModal/ConfirmModal';
import FirstConfirmAccountDelete from '@/components/SharedComponents/FirstConfirmAccountDelete/FirstConfirmAccountDelete';
import { AccountDelete as SecondConfirmAccountDelete } from '@/components/SharedComponents/AccountDelete';
import ThirdConfirmAccountDelete from '@/components/SharedComponents/ThirdConfirmAccountDelete/ThirdConfirmAccountDelete';
import UnrevokedConsentText from '@/components/SharedComponents/UnrevokedConsentText/UnrevokedConsentText';
import colors, { appTheme } from '@/config/colors';
import { updateConsent } from '../utils/consents';

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },

  body: {
    flex: 1,
    padding: 10,
  },
  agreementBlockStyle: {
    borderWidth: 1,
    borderColor: colors.darkGrey,
    backgroundColor: colors.white,
  },
  flexRow: {
    flexDirection: 'row',
  },
  fontBold: {
    fontWeight: '700',
  },
  spaceBetween: {
    justifyContent: 'space-between',
  },
  redFont: {
    color: colors.red,
  },
  greenFont: {
    color: appTheme.primary,
  },
  textError: {
    fontSize: 18,
    paddingTop: 10,
    color: colors.red,
  },
});

function PatientEinwilligungen() {
  const responsiveHeight = useResponsiveHeight(100);
  const isFullscreen = Platform.OS !== 'web' && responsiveHeight < 500;
  const [noConnection, setNoConnection] = useState(false);
  const userConsents = useStoreState((state) => state.userConsents);
  const responsiveFontSize = useResponsiveFontSize(1);
  const [
    showFirstConfirmAccountDeletionModal,
    setFirstShowConfirmAccountDeletionModal,
  ] = useState(false);
  const [
    showSecondConfirmAccountDeletionModal,
    setSecondShowConfirmAccountDeletionModal,
  ] = useState(false);
  const [
    showThirdConfirmAccountDeletionModal,
    setThirdShowConfirmAccountDeletionModal,
  ] = useState(false);
  const [showUnrevokedConsentModal, setShowUnrevokedConsentModal] =
    useState(false);
  const [deactivationPostponeConsentId, setDeactivationPostponeConsentId] =
    useState<string | null>(null);
  const [
    showUserDeactivationPostponeConsentModal,
    setShowUserDeactivationPostponeConsentModal,
  ] = useState(false);
  const [userDeactivationPostponeError, setUserDeactivationPostponeError] =
    useState('');
  const [isDeletionEnabled, setIsDeletionEnabled] = useState(false);

  const getResponsiveFontSize = (factor: number) => responsiveFontSize * factor;

  let responsiveStyles = StyleSheet.create({
    ihreEnwillgungenFontSize: {
      fontSize: getResponsiveFontSize(2),
      textAlign: 'center',
      paddingTop: 20,
      fontWeight: '600',
    },
    blockPadding: {
      padding: getResponsiveFontSize(1),
    },
    agreementText: {
      fontSize: getResponsiveFontSize(1.5),
    },
    agreementTexts: {
      fontSize: getResponsiveFontSize(1.3),
      marginVertical: getResponsiveFontSize(0.5),
    },
    agreementBlockStyle: {
      marginVertical: getResponsiveFontSize(1.4),
    },
    marginLeft: {
      marginLeft: getResponsiveFontSize(0.8),
    },
    marginTop: {
      marginTop: getResponsiveFontSize(1.2),
    },
    contentText: {
      fontSize: useResponsiveFontSize(1.5),
      padding: useResponsiveScreenWidth(2.8),
    },
  });

  useEffect(() => {
    const consents = async () => {
      try {
        await fetchUserConsents();
      } catch (error: any) {
        if (!error.response) {
          setNoConnection(true);
        }
      }
    };
    consents();
  }, []);

  return (
    <ScrollView style={styles.container}>
      <ConfirmModal
        isVisible={showFirstConfirmAccountDeletionModal}
        isFullscreen={isFullscreen}
        cancelText={'Abbrechen'}
        confirmText={'Einwilligung widerrufen und Daten löschen'}
        confirmAction={async () => {
          try {
            setFirstShowConfirmAccountDeletionModal(false);
            // Bugfix on iphone second modal is not showing
            setTimeout(() => {
              setSecondShowConfirmAccountDeletionModal(true);
            }, 400);
          } catch {
            await localLogoutUser();
          }
        }}
        cancelAction={() => setFirstShowConfirmAccountDeletionModal(false)}
        headerText={'Wichtiger Hinweis'}
        redBackgroundConfirmButton={true}
        greenBackgroundCancelButton={true}
      >
        <FirstConfirmAccountDelete />
      </ConfirmModal>

      <ConfirmModal
        isVisible={showSecondConfirmAccountDeletionModal}
        cancelText={'Abbrechen'}
        confirmText={'Unwiderruflich Löschen'}
        confirmAction={() => {
          setThirdShowConfirmAccountDeletionModal(true);
        }}
        cancelAction={() => setSecondShowConfirmAccountDeletionModal(false)}
        headerText={'Alle Daten unwiderruflich löschen'}
        redBackgroundConfirmButton={true}
        greenBackgroundCancelButton={true}
      >
        <SecondConfirmAccountDelete />

        <ConfirmModal
          isVisible={showThirdConfirmAccountDeletionModal}
          cancelText={'Abbrechen'}
          confirmText={'Löschen'}
          confirmAction={async () => {
            try {
              if (isDeletionEnabled === true) {
                await deleteUser();
                setThirdShowConfirmAccountDeletionModal(false);
                setSecondShowConfirmAccountDeletionModal(false);
                await localLogoutUser(true);
              }
            } catch {
              await localLogoutUser();
            }
            setIsDeletionEnabled(false);
          }}
          cancelAction={() => {
            setThirdShowConfirmAccountDeletionModal(false);
            setIsDeletionEnabled(false);
          }}
          headerText={'Löschen bestätigen'}
          redBackgroundConfirmButton={isDeletionEnabled}
          greenBackgroundCancelButton={true}
          isConfirmActionDisabled={!isDeletionEnabled}
          responsiveHeight={30}
        >
          <ThirdConfirmAccountDelete
            onUserInput={(isEnabled: boolean) =>
              setIsDeletionEnabled(isEnabled)
            }
          />
        </ConfirmModal>
      </ConfirmModal>

      <ConfirmModal
        isVisible={noConnection}
        isFullscreen={isFullscreen}
        confirmText={'OK'}
        confirmAction={() => setNoConnection(false)}
        headerText={'Keine Verbindung zum neolexon Server'}
        contentText={
          'Bitte überprüfen Sie Ihre Internetverbindung und versuchen es erneut.'
        }
      />

      <ConfirmModal
        isVisible={showUnrevokedConsentModal}
        isFullscreen={isFullscreen}
        cancelText={'Abbrechen'}
        confirmText={'Einwilligung widerrufen und Daten löschen'}
        confirmAction={async () => {
          try {
            setShowUnrevokedConsentModal(false);
            setTimeout(() => {
              setSecondShowConfirmAccountDeletionModal(true);
            }, 400);
          } catch {
            await localLogoutUser();
          }
        }}
        cancelAction={() => setShowUnrevokedConsentModal(false)}
        headerText={'Wichtiger Hinweis'}
        redBackgroundConfirmButton={true}
        greenBackgroundCancelButton={true}
      >
        <UnrevokedConsentText />
      </ConfirmModal>

      <ConfirmModal
        isVisible={showUserDeactivationPostponeConsentModal}
        cancelText={'Abbrechen'}
        isContentComponent
        contentText={
          <View>
            <Text style={responsiveStyles.contentText}>
              Wenn Sie Ihre Einwilligung widerrufen, wird Ihr Account mit den
              Nutzungsdaten am folgenden Tag nach Ablauf des aktuellen
              Rezeptcodes gelöscht. Die Weiternutzung der App ist dann wieder
              möglich, wenn Sie sich neu registrieren.
            </Text>
            <Text style={styles.textError}>
              {userDeactivationPostponeError}
            </Text>
          </View>
        }
        confirmText={'Einwilligung widerrufen'}
        confirmAction={async () => {
          try {
            if (!deactivationPostponeConsentId) {
              return;
            }
            await updateConsent(deactivationPostponeConsentId, false);
            setShowUserDeactivationPostponeConsentModal(false);
            await fetchUserConsents();
          } catch {
            setUserDeactivationPostponeError(
              'Die Einwilligung konnte nicht aktualisiert werden. Bitte versuchen Sie es später erneut.',
            );
          }
        }}
        cancelAction={() => setShowUserDeactivationPostponeConsentModal(false)}
        headerText={'Einwilligung zur Verlängerung der Datenlöschung'}
        redBackgroundConfirmButton={true}
        greenBackgroundCancelButton={true}
      ></ConfirmModal>

      {/* Body */}
      <View style={styles.body}>
        <Text style={[responsiveStyles.ihreEnwillgungenFontSize]}>
          Ihre Einwilligungen
        </Text>
        {userConsents.map((consent) => {
          return (
            <View
              key={consent._id}
              style={[
                styles.agreementBlockStyle,
                responsiveStyles.blockPadding,
                responsiveStyles.agreementBlockStyle,
              ]}
            >
              <Text style={responsiveStyles.agreementText}>
                {consent.consentText}
              </Text>
              <View
                style={[
                  styles.flexRow,
                  styles.spaceBetween,
                  responsiveStyles.marginTop,
                ]}
              >
                {consent.consentInfoLinkAddress ? (
                  <PressableNeolexon
                    onPress={() => {
                      Linking.openURL(consent.consentInfoLinkAddress);
                    }}
                  >
                    <Text
                      style={[responsiveStyles.agreementTexts, styles.fontBold]}
                    >
                      {consent.consentInfoLinkText}
                    </Text>
                  </PressableNeolexon>
                ) : (
                  <View></View>
                )}

                <PressableNeolexon
                  onPress={async () => {
                    if (consent.name === 'DataProcessing') {
                      setFirstShowConfirmAccountDeletionModal(true);
                    } else if (consent.name === 'PaymentProcessing') {
                      setShowUnrevokedConsentModal(true);
                    } else if (consent.name === 'PlannedUserDeletionPostpone') {
                      if (consent.givenConsent) {
                        setDeactivationPostponeConsentId(consent._id);
                        setShowUserDeactivationPostponeConsentModal(true);
                      } else {
                        await updateConsent(consent._id, !consent.givenConsent);
                        await fetchUserConsents();
                      }
                    } else if (
                      consent.name === 'StudyConsent' ||
                      consent.name === 'WriteToEpa'
                    ) {
                      await updateConsent(consent._id, !consent.givenConsent);
                      await fetchUserConsents();
                    }
                  }}
                >
                  <Text
                    style={[
                      responsiveStyles.agreementTexts,
                      styles.fontBold,
                      consent.givenConsent ? styles.redFont : styles.greenFont,
                    ]}
                  >
                    {consent.givenConsent
                      ? 'Einwilligung widerrufen'
                      : 'Einwilligung erteilen'}
                  </Text>
                </PressableNeolexon>
              </View>
              <View style={styles.flexRow}>
                <Text style={[responsiveStyles.agreementTexts]}>Status:</Text>
                <Text
                  style={[
                    responsiveStyles.agreementTexts,
                    styles.fontBold,
                    consent.givenConsent ? styles.greenFont : styles.redFont,
                    responsiveStyles.marginLeft,
                  ]}
                >
                  {consent.givenConsent ? 'eingewilligt' : 'nicht eingewilligt'}
                </Text>
              </View>
            </View>
          );
        })}
      </View>
    </ScrollView>
  );
}

export default PatientEinwilligungen;
